import { createSlice } from '@reduxjs/toolkit'
import {
  changeRecoveryKey,
  checkIfHasRecoveryKey,
  checkIfNeedsRecoveryKeyInput,
  getChatCredentials,
  startMatrixClient,
  stopMatrixClient,
  refreshMatrixToken,
  setupRecoveryKey,
  restoreWithRecoveryKey,
  fetchAvatarData,
  resetRecoveryKey,
  getDirectRooms,
  getRoomInfo
} from 'store/chat/services'
import {
  PENDING,
  REJECTED,
  SUCCEEDED
} from 'utils/constants'

const initialState = {
  connectionApi: null,
  chatCredentials: null,
  connectedProfile: null,
  meXmppIsOnline: false,
  meXmppProblem: null,
  xmppPushNotification: null,
  numUnread: 0,
  isXmppModalSpinnerHidden: false,
  isMobileImagePickerState: false,
  isMobileImageTransferInprogress: false,
  xmppChatPanelOpened: false,
  forceCloseChatPanel: false,
  matrixStatus: 'idle',
  matrixError: null,
  rooms: [],
  matrixMessagesbyRoomId: {},
  syncState: null,
  xmppLiveLocation: {
    lat: NaN,
    lng: NaN
  },
  recoveryKey: '',
  needsToInputRecoveryKey: false,
  hasRecoveryKey: false,
  onlineMatrixList: [],
  directRoomList: [],
  setupRecoveryKeyStatus: PENDING | SUCCEEDED | REJECTED,
  restoreWithRecoveryKeyStatus: PENDING | SUCCEEDED | REJECTED,
  checkIfHasRecoveryKeyStatus: PENDING | SUCCEEDED | REJECTED,
  changeRecoveryKeyStatus: PENDING | SUCCEEDED | REJECTED,
  checkIfNeedsRecoveryKeyInputStatus: PENDING | SUCCEEDED | REJECTED,
  fetchAvatarDataStatus: PENDING | SUCCEEDED | REJECTED,
  resetRecoveryKeyStatus: PENDING | SUCCEEDED | REJECTED,
  getDirectRoomsStatus: PENDING | SUCCEEDED | REJECTED,
  getRoomInfoStatus: PENDING | SUCCEEDED | REJECTED
}

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setXmppLiveLocation: (state, { payload }) => {
      if (!payload?.latitude || !payload?.longitude) {
        return
      }
      state.xmppLiveLocation.lat = payload.latitude
      state.xmppLiveLocation.lng = payload.longitude
    },
    setXmppPushNotification: (state, { payload }) => {
      state.xmppPushNotification = payload
    },
    clearXmppPushNotification: (state) => {
      state.xmppPushNotification = null
    },
    setXmppChatPanelOpened: (state, { payload }) => {
      state.xmppChatPanelOpened = payload
    },
    setForceCloseChatPanel: (state, { payload }) => {
      state.forceCloseChatPanel = payload
    },
    hideModalSpinner: (state) => {
      state.isXmppModalSpinnerHidden = true
    },
    showModalSpinner: (state) => {
      state.isXmppModalSpinnerHidden = false
    },
    beginMobileImagePicker: (state) => {
      state.isMobileImagePickerState = true
    },
    endMobileImagePicker: (state) => {
      state.isMobileImagePickerState = false
    },
    beginMobileImageTransfer: (state) => {
      state.isMobileImageTransferInprogress = true
    },
    endMobileImageTransfer: (state) => {
      state.isMobileImageTransferInprogress = false
    },
    setMeXmppInitialState: (state) => {
      state.meXmppProblem = null
      state.meXmppIsOnline = false
    },
    setMeXmppProblem: (state, { payload }) => {
      state.meXmppProblem = payload
      console.log(`TODO: implement audit log: ${state.meXmppProblem.status}, ${state.meXmppProblem.message}`)
    },
    setMeXmppOnlineStatus: (state, { payload }) => {
      state.meXmppIsOnline = payload
    },
    openConnectedProfileChat: (state, { payload }) => {
      state.connectedProfile = payload
      const event = new CustomEvent('custom-chat-open_chat_with_friend', {
        detail: {
          recipient: payload
        }
      })
      window.dispatchEvent(event)
    },
    clearConnectedProfileChat: (state) => {
      state.connectedProfile = null
    },
    changeNumUnread: (state, { payload }) => {
      state.numUnread = payload
    },
    messageReceived: (state, action) => {
      const {
        roomId, sender, body, ts
      } = action.payload
      if (!state.matrixMessagesbyRoomId[roomId]) state.matrixMessagesbyRoomId[roomId] = []
      state.matrixMessagesbyRoomId[roomId].push({ sender, body, ts })
    },
    syncStateChanged: (state, action) => {
      state.syncState = action.payload
    },
    setRecoveryKey: (state, action) => {
      state.recoveryKey = action.payload
    },
    setNeedsToInputRecoveryKey: (state, action) => {
      state.needsToInputRecoveryKey = action.payload
    },
    setRestoreWithRecoveryKeyStatus: (state, action) => {
      state.restoreWithRecoveryKeyStatus = action.payload
    },
    setOnlineMatrixList: (state, action) => {
      if (action.payload?.presence === 'online') {
        const found = state.onlineMatrixList.find((userId) => userId === action.payload?.userId)

        if (!found) {
          state.onlineMatrixList.push(action.payload?.userId)
        }
      } else if (action.payload?.presence === 'offline') {
        const onlineList = state.onlineMatrixList
        state.onlineMatrixList = onlineList.filter((oml) => oml !== action.payload?.userId)
      }
    },
    setResetRecoveryKeyStatus: (state, action) => {
      state.resetRecoveryKeyStatus = action.payload
    },
    setDirectRoomList: (state, action) => {
      if (action.payload?.type === 'clear') {
        state.directRoomList = []
      } else if (action.payload?.type === 'add') {
        state.directRoomList.splice(1, 0, { roomId: action.payload?.roomId })
      } else if (action.payload?.type === 'remove') {
        const modified = state.directRoomList.filter((drl) => drl.roomId !== action.payload?.roomId)
        state.directRoomList = modified
      }
    },
    setMatrixAccNameChange: (state, action) => {
      let found = null
      const {
        name,
        roomId
      } = action.payload

      if (action.payload) {
        found = state.directRoomList.find((r) => r?.roomId === roomId)

        if (found) {
          found.name = name
        }

        // Check on groups
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getChatCredentials.fulfilled, (state, { payload }) => {
        state.chatCredentials = payload
      })
    // start client
      .addCase(startMatrixClient.pending, (state) => {
        state.status = 'starting'
      })
      .addCase(startMatrixClient.fulfilled, (state) => {
        state.status = 'ready'
      })
      .addCase(startMatrixClient.rejected, (state, action) => {
        state.status = 'error'
        state.error = action.payload
      })
      // stop client
      .addCase(stopMatrixClient.fulfilled, (state) => {
        state.status = 'idle'
        state.rooms = []
        state.messagesByRoom = {}
      })
      .addCase(refreshMatrixToken.fulfilled, (state, action) => {
        state.credentials = {
          ...state.credentials,
          accessToken: action.payload.accessToken,
          refreshToken: action.payload.refreshToken
        }
      })
      .addCase(refreshMatrixToken.rejected, (state, action) => {
        state.status = 'error'
        state.error = action.payload
      // refresh token itself may be invalid here -- likely needs a full re-login
      })
      // Checks if the profile has a recovery key already
      .addCase(checkIfHasRecoveryKey.pending, (state) => {
        state.checkIfHasRecoveryKeyStatus = PENDING
      })
      .addCase(checkIfHasRecoveryKey.fulfilled, (state, { payload }) => {
        state.checkIfHasRecoveryKeyStatus = SUCCEEDED
        state.hasRecoveryKey = payload
      })
      .addCase(checkIfHasRecoveryKey.rejected, (state) => {
        state.checkIfHasRecoveryKeyStatus = REJECTED
      })
      // Generate a recovery key for the profile
      .addCase(setupRecoveryKey.pending, (state) => {
        state.setupRecoveryKeyStatus = PENDING
      })
      .addCase(setupRecoveryKey.fulfilled, (state, { payload }) => {
        state.setupRecoveryKeyStatus = SUCCEEDED
        state.recoveryKey = payload
        state.hasRecoveryKey = true
        state.needsToInputRecoveryKey = false
      })
      .addCase(setupRecoveryKey.rejected, (state) => {
        state.setupRecoveryKeyStatus = REJECTED
      })
      // Regenerate a recovery key for the profile
      .addCase(changeRecoveryKey.pending, (state) => {
        state.changeRecoveryKeyStatus = PENDING
      })
      .addCase(changeRecoveryKey.fulfilled, (state, { payload }) => {
        state.changeRecoveryKeyStatus = SUCCEEDED
        state.recoveryKey = payload
      })
      .addCase(changeRecoveryKey.rejected, (state) => {
        state.changeRecoveryKeyStatus = REJECTED
      })
      // Restore chat using recovery key
      .addCase(restoreWithRecoveryKey.pending, (state) => {
        state.restoreWithRecoveryKeyStatus = PENDING
      })
      .addCase(restoreWithRecoveryKey.fulfilled, (state) => {
        state.restoreWithRecoveryKeyStatus = SUCCEEDED
        state.needsToInputRecoveryKey = false
      })
      .addCase(restoreWithRecoveryKey.rejected, (state) => {
        state.restoreWithRecoveryKeyStatus = REJECTED
      })
      // Check if the account needs to input their recovery key. Ex: To decrypt old msgs
      .addCase(checkIfNeedsRecoveryKeyInput.pending, (state) => {
        state.checkIfNeedsRecoveryKeyInputStatus = PENDING
      })
      .addCase(checkIfNeedsRecoveryKeyInput.fulfilled, (state, { payload }) => {
        state.checkIfNeedsRecoveryKeyInputStatus = SUCCEEDED
        state.needsToInputRecoveryKey = payload
      })
      .addCase(checkIfNeedsRecoveryKeyInput.rejected, (state) => {
        state.checkIfNeedsRecoveryKeyInputStatus = REJECTED
      })
      // Fetch the avatar from Matrix
      .addCase(fetchAvatarData.pending, (state) => {
        state.fetchAvatarDataStatus = PENDING
      })
      .addCase(fetchAvatarData.fulfilled, (state, action) => {
        const { roomId } = action.meta.arg
        let found = null

        if (action.payload) {
          found = state.directRoomList.find((r) => r?.roomId === roomId)

          if (found) {
            found.avatarUrl = action.payload
          }

          // Check on groups
        }

        state.fetchAvatarDataStatus = SUCCEEDED
      })
      .addCase(fetchAvatarData.rejected, (state) => {
        state.fetchAvatarDataStatus = REJECTED
      })
      // Allow cross signing replacement (Reset identity without being a verified device)
      .addCase(resetRecoveryKey.pending, (state) => {
        state.resetRecoveryKeyStatus = PENDING
      })
      .addCase(resetRecoveryKey.fulfilled, (state, { payload }) => {
        state.resetRecoveryKeyStatus = SUCCEEDED
        state.recoveryKey = payload
        state.hasRecoveryKey = true
        state.needsToInputRecoveryKey = false
      })
      .addCase(resetRecoveryKey.rejected, (state) => {
        state.resetRecoveryKeyStatus = REJECTED
      })
      // Get direct rooms
      .addCase(getDirectRooms.pending, (state) => {
        state.getDirectRoomsStatus = PENDING
      })
      .addCase(getDirectRooms.fulfilled, (state, { payload }) => {
        state.directRoomList = payload

        state.getDirectRoomsStatus = SUCCEEDED
      })
      .addCase(getDirectRooms.rejected, (state) => {
        state.getDirectRoomsStatus = REJECTED
      })
      // Get room info
      .addCase(getRoomInfo.pending, (state) => {
        state.getRoomInfoStatus = PENDING
      })
      .addCase(getRoomInfo.fulfilled, (state, { payload }) => {
        if (payload?.type === 'direct') {
          state.directRoomList.splice(1, 0, payload)
        }

        state.getRoomInfoStatus = SUCCEEDED
      })
      .addCase(getRoomInfo.rejected, (state) => {
        state.getRoomInfoStatus = REJECTED
      })
  }
})

export const {
  setXmppLiveLocation,
  setXmppPushNotification,
  clearXmppPushNotification,
  setMeXmppInitialState,
  setMeXmppProblem,
  setMeXmppOnlineStatus,
  openConnectedProfileChat,
  clearConnectedProfileChat,
  changeNumUnread,
  hideModalSpinner,
  showModalSpinner,
  beginMobileImagePicker,
  endMobileImagePicker,
  beginMobileImageTransfer,
  endMobileImageTransfer,
  setXmppChatPanelOpened,
  setForceCloseChatPanel,
  messageReceived,
  syncStateChanged,
  setRecoveryKey,
  setNeedsToInputRecoveryKey,
  setRestoreWithRecoveryKeyStatus,
  setOnlineMatrixList,
  setResetRecoveryKeyStatus,
  setDirectRoomList,
  setMatrixAccNameChange
} = chatSlice.actions

export const chatReducer = chatSlice.reducer
